import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory, getPopularArticles } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');

  if (!category) {
    return <NotFound />;
  }

  const categoryArticles = getArticlesByCategory(category.slug);
  const popularArticles = getPopularArticles(4);

  const schemaData = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: `${category.name} - 福利社导航`,
      description: category.description,
      url: `https://fulishe-dh.com/category/${category.slug}`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://fulishe-dh.com/' },
          { '@type': 'ListItem', position: 2, name: category.name, item: `https://fulishe-dh.com/category/${category.slug}` },
        ],
      },
    },
  ];

  return (
    <>
      <SEOHead
        title={category.name}
        description={category.description}
        keywords={`福利社导航,${category.name},福利资源导航,福利社精选推荐,福利导航大全`}
        canonicalUrl={`https://fulishe-dh.com/category/${category.slug}`}
        schemaData={schemaData}
      />

      <main className="pt-16">
        {/* Category Header */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-2">
              <div className="w-14 h-14 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                <i className={`${category.icon} text-primary-600 text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-xl md:text-3xl font-bold text-foreground-950 font-heading mb-1">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-600 max-w-lg">
                  {category.description}
                </p>
              </div>
              <div className="sm:ml-auto flex items-center gap-2 text-sm text-foreground-500">
                <span className="bg-background-50 border border-background-200/70 rounded-full px-3 py-1">
                  共 <strong className="text-primary-600">{category.count}</strong> 个资源
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="sticky top-16 z-40 bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    cat.slug === category.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Main Content */}
              <div className="flex-1">
                {categoryArticles.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {categoryArticles.map((article) => (
                      <Link
                        key={article.id}
                        to={`/article/${article.id}`}
                        className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
                      >
                        <div className="relative h-48 overflow-hidden">
                          <img
                            src={article.image}
                            alt={article.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-4">
                          <h3 className="text-sm font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
                            {article.title}
                          </h3>
                          <p className="text-xs text-foreground-500 line-clamp-2 mb-3 leading-relaxed">
                            {article.description}
                          </p>
                          <div className="flex items-center justify-between text-xs text-foreground-400">
                            <span>{article.date}</span>
                            <span className="flex items-center gap-1">
                              <i className="ri-eye-line"></i> {article.views.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
                      <i className="ri-inbox-line text-foreground-400 text-2xl"></i>
                    </div>
                    <p className="text-sm text-foreground-500">该分类暂无资源，敬请期待更新</p>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <aside className="w-full lg:w-72 flex-shrink-0">
                <div className="lg:sticky lg:top-28">
                  <div className="bg-background-100 rounded-lg p-5 mb-5">
                    <h3 className="text-sm font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                      <i className="ri-fire-line text-accent-500"></i>
                      热门推荐
                    </h3>
                    <div className="space-y-4">
                      {popularArticles.map((article, idx) => (
                        <Link
                          key={article.id}
                          to={`/article/${article.id}`}
                          className="flex items-start gap-3 group cursor-pointer"
                        >
                          <span className="text-lg font-bold text-foreground-300 font-heading flex-shrink-0 leading-none">
                            {String(idx + 1).padStart(2, '0')}
                          </span>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-xs font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                              {article.title}
                            </h4>
                            <span className="text-xs text-foreground-400 mt-1">{article.views.toLocaleString()} 次浏览</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>

                  <div className="bg-background-100 rounded-lg p-5">
                    <h3 className="text-sm font-semibold text-foreground-950 mb-3 flex items-center gap-2">
                      <i className="ri-price-tag-3-line text-primary-500"></i>
                      热门标签
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {['视频会员', '优惠券', '游戏礼包', '购物返利', '信用卡', '外卖红包', 'VIP权益', '免费课程'].map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1 bg-background-50 border border-background-200/70 rounded-full text-xs text-foreground-600 hover:text-primary-600 hover:border-primary-300 transition-colors cursor-pointer"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}